import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonModal,
  IonPage,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar,
  IonAlert,
  IonSpinner,
} from "@ionic/react";
import { add, chevronBack, chevronForward, documentTextOutline, trashOutline } from "ionicons/icons";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { apiFetch } from "../api/client.js";
import {
  clearTeamInvoiceSettingsCache,
  clearTeamsCache,
  fetchTeamInvoiceSettings,
  fetchTeams,
} from "../api/teams.js";
import AddressAutocompleteField from "../components/AddressAutocompleteField.jsx";

const blankInvoiceSettings = {
  businessName: "",
  legalName: "",
  businessAddress: "",
  businessPhone: "",
  businessEmail: "",
  taxLabel: "ABN",
  taxNumber: "",
  paymentLabel: "PayID",
  paymentValue: "",
  bankDetails: "",
  terms: "",
  logoUrl: "",
  templateStyle: "AU_STANDARD",
};

export default function TeamsPage() {
  const history = useHistory();
  const [teams, setTeams] = useState([]);
  const [name, setName] = useState("");
  const [toast, setToast] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [editName, setEditName] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [invoiceSettingsTeam, setInvoiceSettingsTeam] = useState(null);
  const [invoiceSettings, setInvoiceSettings] = useState(blankInvoiceSettings);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  async function load() {
    const teamList = await fetchTeams();

    setTeams(
      [...teamList].sort((first, second) => {
        if (first.isDefault !== second.isDefault) {
          return first.isDefault ? -1 : 1;
        }

        return new Date(first.createdAt).getTime() - new Date(second.createdAt).getTime();
      }),
    );
  }

  useEffect(() => {
    load().catch((err) => setToast(err.message));
  }, []);

  async function save() {
    try {
      await apiFetch("/teams", { method: "POST", body: { name } });
      clearTeamsCache();
      setName("");
      setIsCreateOpen(false);
      await load();
      setToast("Team created.");
    } catch (err) {
      setToast(err.message);
    }
  }

  function openTeam(team) {
    setSelectedTeam(team);
    setEditName(team.name);
    setIsDefault(team.isDefault);
  }

  async function updateTeam() {
    try {
      await apiFetch(`/teams/${selectedTeam.id}`, {
        method: "PATCH",
        body: { name: editName },
      });

      if (isDefault && !selectedTeam.isDefault) {
        await apiFetch(`/teams/default/${selectedTeam.id}`, { method: "POST" });
        localStorage.setItem("metora_team_id", selectedTeam.id);
      }

      clearTeamsCache();
      setSelectedTeam(null);
      await load();
      setToast("Team updated.");
    } catch (err) {
      setToast(err.message);
    }
  }

  async function deleteTeam() {
    try {
      const result = await apiFetch(`/teams/${selectedTeam.id}`, { method: "DELETE" });

      if (localStorage.getItem("metora_team_id") === selectedTeam.id && result.defaultTeamId) {
        localStorage.setItem("metora_team_id", result.defaultTeamId);
      }

      clearTeamsCache();
      setIsDeleteOpen(false);
      setSelectedTeam(null);
      await load();
      setToast("Team deleted.");
    } catch (err) {
      setIsDeleteOpen(false);
      setToast(err.message);
    }
  }

  function viewUsers() {
    const team = selectedTeam;

    setSelectedTeam(null);
    history.push(`/app/profile/teams/${team.id}/users`, { team });
  }

  async function openInvoiceSettings() {
    try {
      const team = selectedTeam;
      const settings = await fetchTeamInvoiceSettings(team.id);

      setSelectedTeam(null);
      setInvoiceSettingsTeam(team);
      setInvoiceSettings({
        ...blankInvoiceSettings,
        ...Object.fromEntries(
          Object.entries(settings).map(([key, value]) => [key, value ?? ""]),
        ),
      });
    } catch (err) {
      setToast(err.message);
    }
  }

  async function saveInvoiceSettings() {
    try {
      await apiFetch(`/teams/invoice-settings/${invoiceSettingsTeam.id}`, {
        method: "PUT",
        body: invoiceSettings,
      });

      clearTeamInvoiceSettingsCache(invoiceSettingsTeam.id);
      setInvoiceSettingsTeam(null);
      setToast("Invoice settings updated.");
    } catch (err) {
      setToast(err.message);
    }
  }

  async function uploadLogo(file) {
    if (!file || !invoiceSettingsTeam) {
      return;
    }

    try {
      setIsUploadingLogo(true);
      const formData = new FormData();
      formData.append("logo", file);

      const result = await apiFetch(`/teams/invoice-settings/logo/${invoiceSettingsTeam.id}`, {
        method: "POST",
        body: formData,
      });

      clearTeamInvoiceSettingsCache(invoiceSettingsTeam.id);
      setInvoiceSettings((current) => ({
        ...current,
        logoUrl: result.logoUrl,
      }));
      setToast("Logo uploaded.");
    } catch (err) {
      setToast(err.message);
    } finally {
      setIsUploadingLogo(false);

      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
    }
  }

  async function clearLogo() {
    if (!invoiceSettingsTeam) {
      return;
    }

    try {
      setIsUploadingLogo(true);
      await apiFetch(`/teams/invoice-settings/logo/${invoiceSettingsTeam.id}`, {
        method: "DELETE",
      });
      clearTeamInvoiceSettingsCache(invoiceSettingsTeam.id);
      setInvoiceSettings((current) => ({
        ...current,
        logoUrl: "",
      }));
      setToast("Logo removed.");
    } catch (err) {
      setToast(err.message);
    } finally {
      setIsUploadingLogo(false);
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={() => history.goBack()} aria-label="Back">
              <IonIcon icon={chevronBack} />
            </IonButton>
          </IonButtons>
          <IonTitle>My Teams</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => setIsCreateOpen(true)} aria-label="Create team">
              <IonIcon icon={add} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <main className="page-pad">
          <section className="list-stack">
            {teams.map((team) => (
              <button className="panel row-card list-button" key={team.id} onClick={() => openTeam(team)}>
                <div>
                  <h3>{team.name}</h3>
                  <p>
                    {team.isDefault ? "Default team" : "Team"} · {team.isActive ? "Active" : "Inactive"}
                  </p>
                </div>
                <IonIcon icon={chevronForward} />
              </button>
            ))}
            {teams.length === 0 && <div className="empty-state">No teams created.</div>}
          </section>
        </main>

        <IonModal isOpen={isCreateOpen} onDidDismiss={() => setIsCreateOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Create Team</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setIsCreateOpen(false)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <main className="page-pad">
              <section className="panel form-grid">
                <IonInput
                  className="metora-input"
                  label="Team Name"
                  labelPlacement="stacked"
                  placeholder="Enter team name"
                  value={name}
                  onIonInput={(event) => setName(event.detail.value ?? "")}
                />
                <IonButton expand="block" onClick={save} disabled={!name.trim()}>
                  Create Team
                </IonButton>
              </section>
            </main>
          </IonContent>
        </IonModal>

        <IonModal isOpen={Boolean(selectedTeam)} onDidDismiss={() => setSelectedTeam(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Manage Team</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setSelectedTeam(null)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <main className="page-pad">
              <section className="panel form-grid">
                <IonInput
                  className="metora-input"
                  label="Team Name"
                  labelPlacement="stacked"
                  placeholder="Enter team name"
                  value={editName}
                  onIonInput={(event) => setEditName(event.detail.value ?? "")}
                />
                <label className="checkbox-row">
                  <IonCheckbox
                    checked={isDefault}
                    disabled={selectedTeam?.isDefault}
                    onIonChange={(event) => setIsDefault(event.detail.checked)}
                  />
                  <span>{selectedTeam?.isDefault ? "Default team" : "Set as default team"}</span>
                </label>
                <IonButton expand="block" fill="outline" onClick={viewUsers}>
                  View Users
                  <IonIcon icon={chevronForward} slot="end" />
                </IonButton>
                <IonButton expand="block" fill="outline" onClick={openInvoiceSettings}>
                  <IonIcon icon={documentTextOutline} slot="start" />
                  Invoice Settings
                </IonButton>
                <IonButton expand="block" onClick={updateTeam} disabled={!editName.trim()}>
                  Update Team
                </IonButton>
                <IonButton expand="block" color="danger" fill="outline" onClick={() => setIsDeleteOpen(true)}>
                  <IonIcon icon={trashOutline} slot="start" />
                  Delete Team
                </IonButton>
              </section>
            </main>
          </IonContent>
        </IonModal>

        <IonModal isOpen={Boolean(invoiceSettingsTeam)} onDidDismiss={() => setInvoiceSettingsTeam(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Invoice Settings</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setInvoiceSettingsTeam(null)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <main className="page-pad">
              <section className="panel form-grid">
                <IonInput
                  className="metora-input"
                  label="Business Name"
                  labelPlacement="stacked"
                  placeholder="Enter business name"
                  value={invoiceSettings.businessName}
                  onIonInput={(event) => setInvoiceSettings({ ...invoiceSettings, businessName: event.detail.value ?? "" })}
                />
                <IonInput
                  className="metora-input"
                  label="Legal Name"
                  labelPlacement="stacked"
                  placeholder="Enter legal name"
                  value={invoiceSettings.legalName}
                  onIonInput={(event) => setInvoiceSettings({ ...invoiceSettings, legalName: event.detail.value ?? "" })}
                />
                <AddressAutocompleteField
                  label="Business Address"
                  placeholder="Enter business address"
                  value={invoiceSettings.businessAddress}
                  onChange={(businessAddress) => setInvoiceSettings({ ...invoiceSettings, businessAddress })}
                />
                <IonInput
                  className="metora-input"
                  label="Business Phone"
                  labelPlacement="stacked"
                  type="tel"
                  placeholder="Enter business phone"
                  value={invoiceSettings.businessPhone}
                  onIonInput={(event) => setInvoiceSettings({ ...invoiceSettings, businessPhone: event.detail.value ?? "" })}
                />
                <IonInput
                  className="metora-input"
                  label="Business Email"
                  labelPlacement="stacked"
                  type="email"
                  placeholder="Enter business email"
                  value={invoiceSettings.businessEmail}
                  onIonInput={(event) => setInvoiceSettings({ ...invoiceSettings, businessEmail: event.detail.value ?? "" })}
                />
                <IonInput
                  className="metora-input"
                  label="Tax Label"
                  labelPlacement="stacked"
                  placeholder="ABN"
                  value={invoiceSettings.taxLabel}
                  onIonInput={(event) => setInvoiceSettings({ ...invoiceSettings, taxLabel: event.detail.value ?? "" })}
                />
                <IonInput
                  className="metora-input"
                  label="Tax Number"
                  labelPlacement="stacked"
                  placeholder="Enter ABN or tax number"
                  value={invoiceSettings.taxNumber}
                  onIonInput={(event) => setInvoiceSettings({ ...invoiceSettings, taxNumber: event.detail.value ?? "" })}
                />
                <IonInput
                  className="metora-input"
                  label="Payment Label"
                  labelPlacement="stacked"
                  placeholder="PayID"
                  value={invoiceSettings.paymentLabel}
                  onIonInput={(event) => setInvoiceSettings({ ...invoiceSettings, paymentLabel: event.detail.value ?? "" })}
                />
                <IonInput
                  className="metora-input"
                  label="Payment Value"
                  labelPlacement="stacked"
                  placeholder="Enter PayID"
                  value={invoiceSettings.paymentValue}
                  onIonInput={(event) => setInvoiceSettings({ ...invoiceSettings, paymentValue: event.detail.value ?? "" })}
                />
                <IonInput
                  className="metora-input"
                  label="Bank Details"
                  labelPlacement="stacked"
                  placeholder="BSB - 000000 Account number - 000000"
                  value={invoiceSettings.bankDetails}
                  onIonInput={(event) => setInvoiceSettings({ ...invoiceSettings, bankDetails: event.detail.value ?? "" })}
                />
                <IonTextarea
                  className="metora-input"
                  label="Terms"
                  labelPlacement="stacked"
                  placeholder="Enter payment terms"
                  autoGrow
                  value={invoiceSettings.terms}
                  onIonInput={(event) => setInvoiceSettings({ ...invoiceSettings, terms: event.detail.value ?? "" })}
                />
                <section className="panel form-grid">
                  <div>
                    <h3>Invoice Logo</h3>
                    <p className="muted-copy">Upload a square or rectangular image. It will be resized and compressed automatically.</p>
                  </div>

                  {invoiceSettings.logoUrl ? (
                    <div className="team-logo-preview">
                      <img src={invoiceSettings.logoUrl} alt="Invoice logo preview" />
                    </div>
                  ) : (
                    <div className="team-logo-empty">No logo uploaded.</div>
                  )}

                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    hidden
                    onChange={(event) => uploadLogo(event.target.files?.[0])}
                  />

                  <div className="inline-actions">
                    <IonButton
                      expand="block"
                      fill="outline"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={isUploadingLogo}
                    >
                      {isUploadingLogo ? <IonSpinner name="crescent" /> : "Upload Logo"}
                    </IonButton>
                    <IonButton
                      expand="block"
                      fill="outline"
                      color="medium"
                      onClick={clearLogo}
                      disabled={isUploadingLogo || !invoiceSettings.logoUrl}
                    >
                      Remove Logo
                    </IonButton>
                  </div>
                </section>
                <IonButton expand="block" onClick={saveInvoiceSettings}>
                  Save Invoice Settings
                </IonButton>
              </section>
            </main>
          </IonContent>
        </IonModal>

        <IonAlert
          cssClass="metora-alert"
          isOpen={isDeleteOpen}
          header={`Delete ${selectedTeam?.name ?? "team"}?`}
          message="This permanently deletes the team and its users, clients, jobs, time entries, and invoices."
          buttons={[
            {
              text: "Cancel",
              role: "cancel",
              handler: () => setIsDeleteOpen(false),
            },
            {
              text: "Delete Team",
              role: "destructive",
              handler: deleteTeam,
            },
          ]}
          onDidDismiss={() => setIsDeleteOpen(false)}
        />

        <IonToast isOpen={Boolean(toast)} message={toast} duration={2500} onDidDismiss={() => setToast("")} />
      </IonContent>
    </IonPage>
  );
}
